import {
  Stack,
  Text,
  Heading,
  Label,
  InlineStack,
  Panel,
  PanelSection,
  Paragraph,
  FormattedDateTime,
  PanelHeader,
} from '@customerio/pluma-components/react';
import {
  HealthScoreGauge,
  useWorkspaceHealthSimulation,
  useEnvHealth,
  getHealthSummary,
} from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useMemo } from 'react';
import { useWorkspaceDetails } from '@/features/masquerade-banner/hooks';
import { isEmptyString } from '@/utils/string';

export function SummaryPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data?.body;
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;
  const { heading, severities, hasIssues, info } = getHealthSummary(data, isIdentifiersMigrationInProgress);

  const lastUpdated = useMemo(
    () => (healthQuery.dataUpdatedAt > 0 ? new Date(healthQuery.dataUpdatedAt) : new Date()),
    [healthQuery.dataUpdatedAt],
  );

  return (
    <Panel>
      <PanelHeader pb="050">
        <Stack gap="200">
          <HealthScoreGauge size={84 * 0.66} />
          <Heading level="2">{heading}</Heading>
        </Stack>
      </PanelHeader>
      <PanelSection>
        <Stack gap="200">
          {info.length > 0
            ? info.map((item) => (
                <Text color="subtle" size="s" key={item}>
                  {item}
                </Text>
              ))
            : null}
          {hasIssues ? (
            <InlineStack gap="100" wrap>
              {severities.high > 0 ? <Label color="red">{severities.high} high priority</Label> : null}
              {severities.medium > 0 ? <Label color="yellow">{severities.medium} medium</Label> : null}
              {severities.low > 0 ? <Label color="grey">{severities.low} low</Label> : null}
            </InlineStack>
          ) : null}
          <Stack gap="025">
            <Paragraph sizeVariant="xs" variant="secondary">
              Last updated{' '}
              <FormattedDateTime
                shouldUppercaseFirst={false}
                withRelativeDateFromNow={true}
                format="full_with_time"
                date={lastUpdated}
              />
            </Paragraph>
          </Stack>
        </Stack>
      </PanelSection>
    </Panel>
  );
}
